import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Form, Formik, FormikErrors } from 'formik';
import moment from 'moment';
import { OccupationReferenceExtended } from '@sdir/httpclient/lib/clients/aps/seagoing';
import { SdirModal } from '@sdir/sds';
import { useNumberService } from '@sdir/utilities/lib/hooks';
import { AddedSeagoingDetails } from '../../../../../types';
import {
  createSeagoingDetailsInitialValues,
  SeagoingDetailsInput,
  SeagoingDetailsSubForm,
  validateSeagoingDetails,
} from '../SeagoingDetailsSubForm';

type EditDetailsInput = {
  editdetails: SeagoingDetailsInput;
};

type EditDetailsModalProps = {
  isOpen: boolean;
  occupations: OccupationReferenceExtended[];
  onClose: () => void;
  seagoingDetails?: AddedSeagoingDetails;
  onConfirm: (details?: SeagoingDetailsInput) => void;
};

export const EditDetailsModal: FC<EditDetailsModalProps> = ({
  isOpen,
  occupations,
  seagoingDetails,
  onClose,
  onConfirm,
}) => {
  const intl = useIntl();
  const numberService = useNumberService();

  const initialValues = useMemo(() => {
    const details = createSeagoingDetailsInitialValues();
    const periodDiff = seagoingDetails?.endDate?.diff(seagoingDetails?.startDate);
    const totalDays = moment.duration(periodDiff).asDays() + 1;
    const hoursPerDay = (seagoingDetails?.totalHours || 0) / totalDays;
    return {
      editdetails: {
        ...details,
        occupationCode: seagoingDetails?.occupationCode || '',
        startDate: seagoingDetails?.startDate || null,
        endDate: seagoingDetails?.endDate || null,
        totalHours: `${seagoingDetails?.totalHours || ''}`,
        hoursPerDay: `${hoursPerDay}`,
      },
    };
  }, [seagoingDetails]);

  const handleSubmit = useCallback(
    (values: EditDetailsInput) => {
      onConfirm(values?.editdetails);
    },
    [onConfirm]
  );

  const handleValidate = useCallback(
    (values: EditDetailsInput) => {
      let errors: FormikErrors<SeagoingDetailsInput> = {};

      const detailsErrors = validateSeagoingDetails(values.editdetails, intl, numberService);
      if (detailsErrors) errors = detailsErrors;

      return Object.keys(errors).length ? { editdetails: errors } : undefined;
    },
    [intl, numberService]
  );

  if (!initialValues?.editdetails?.occupationCode) return <></>;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidate}>
      {({ submitForm }) => (
        <StyledModal
          visible={isOpen}
          onCancel={onClose}
          onOK={submitForm}
          cancelText={intl.formatMessage({ id: 'general.form.cancel' })}
          okText={intl.formatMessage({ id: 'general.form.save' })}
        >
          <Form>
            <SeagoingDetailsSubForm name="editdetails" occupations={occupations} />
          </Form>
        </StyledModal>
      )}
    </Formik>
  );
};

const StyledModal = styled(SdirModal)`
  font-size: 16px;
`;

export default EditDetailsModal;
